import React, { Component, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { postError } from '../api/telemetry';

interface ErrorBoundaryProps {
    children: ReactNode;
    fallback?: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        postError(
            'REACT_ERROR_BOUNDARY',
            error.message,
            errorInfo.componentStack?.slice(0, 500) ?? undefined
        ).catch(() => { /* telemetry is best-effort */ });

        if (process.env.NODE_ENV === 'development') {
            console.error('ErrorBoundary caught an error:', error, errorInfo);
        }
    }

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) return this.props.fallback;

            return (
                <div
                    className="relative min-h-screen flex items-center justify-center p-8 overflow-hidden"
                    style={{ background: 'var(--c-cream)' }}
                >
                    {/* Ambient orb */}
                    <div
                        className="absolute pointer-events-none"
                        style={{
                            top: '10%', right: '-4%',
                            width: 560, height: 560, borderRadius: '50%',
                            background: 'radial-gradient(circle at 30% 30%, var(--c-coral) 0%, var(--c-kivo-teal) 55%, transparent 72%)',
                            filter: 'blur(96px)',
                            opacity: 0.22,
                        }}
                    />
                    <div
                        className="absolute pointer-events-none"
                        style={{
                            bottom: '5%', left: '-8%',
                            width: 400, height: 400, borderRadius: '50%',
                            background: 'radial-gradient(circle at 60% 60%, var(--c-kivo-teal) 0%, transparent 65%)',
                            filter: 'blur(80px)',
                            opacity: 0.14,
                        }}
                    />

                    {/* Scan-wave texture */}
                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='32' viewBox='0 0 240 32'><path d='M0 16 C 30 4, 60 4, 90 16 S 150 28, 180 16 210 4, 240 16' fill='none' stroke='%232AA4A6' stroke-opacity='0.12' stroke-width='1.1' stroke-linecap='round'/></svg>")`,
                            backgroundRepeat: 'repeat',
                            backgroundSize: '240px 32px',
                        }}
                    />

                    <motion.div
                        initial={{ opacity: 0, y: 28 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.56, ease: [0.22, 0.61, 0.36, 1] }}
                        className="relative z-10 max-w-sm w-full text-center"
                    >
                        {/* Icon ring */}
                        <motion.div
                            initial={{ scale: 0.75, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.1, duration: 0.44, ease: [0.22, 0.61, 0.36, 1] }}
                            className="mx-auto mb-10 w-20 h-20 rounded-full flex items-center justify-center"
                            style={{
                                background: 'white',
                                border: '1px solid var(--c-charcoal-06)',
                                boxShadow: 'var(--sh-lifted)',
                            }}
                        >
                            <span className="material-symbols-outlined text-4xl" style={{ color: 'var(--c-coral)' }}>
                                warning
                            </span>
                        </motion.div>

                        {/* Heading */}
                        <motion.h1
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.18, duration: 0.44, ease: [0.22, 0.61, 0.36, 1] }}
                            className="font-display font-medium text-[40px] leading-[1.08] text-[var(--ink)] mb-4"
                            style={{ letterSpacing: 'var(--tracking-display)' }}
                        >
                            Something<br />
                            <em className="not-italic" style={{ color: 'var(--c-kivo-teal)' }}>went wrong</em>
                        </motion.h1>

                        {/* Error message */}
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.26, duration: 0.4 }}
                            className="font-text text-[14px] leading-[1.55] mb-10"
                            style={{ color: 'rgba(31,36,33,0.55)' }}
                        >
                            {this.state.error?.message ?? 'An unexpected error occurred. Please try reloading the page.'}
                        </motion.p>

                        {/* CTA button */}
                        <motion.button
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.34, duration: 0.4, ease: [0.22, 0.61, 0.36, 1] }}
                            whileHover={{ y: -1 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => { window.location.reload(); }}
                            className="btn btn--primary inline-flex items-center gap-2 font-text font-medium text-[14px] px-7 py-3.5 rounded-full"
                            style={{
                                boxShadow: 'var(--sh-deep)',
                                letterSpacing: '0.01em',
                            }}
                        >
                            <span className="material-symbols-outlined text-[18px]">refresh</span>
                            Reload page
                        </motion.button>

                        {/* Decorative rule */}
                        <div
                            className="mt-14 w-20 h-px mx-auto"
                            style={{ background: 'linear-gradient(90deg, transparent, rgba(42,164,166,0.45), transparent)' }}
                        />
                        <p
                            className="mt-4 font-text text-[11px] tracking-[0.18em] uppercase"
                            style={{ color: 'rgba(31,36,33,0.28)' }}
                        >
                            kivo.skin
                        </p>
                    </motion.div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
